/**
 * iter47g — Animated "living" rank chart for the Tournament Recap.
 *
 * Inspired by livingcharts.com — a bump chart that smoothly animates rank
 * changes between matches, with player labels that ride along the lines.
 *
 * Data shape (from /api/leagues/:id/recap → `match_series`):
 *   keys:    [{participant_id, username}]
 *   matches: [{index, label, score, played_at, ...}]
 *   rows:    [{match_index: 0, ranks: {pid: rank}, points: {pid: pts}}, ...]
 *
 * Controls:
 *   ▶ / ⏸  — play / pause auto-advance
 *   ⏮     — restart
 *   slider — manual scrub to any match
 *   toggle — Rank ↔ Points y-axis
 */
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, RotateCcw, Maximize2, Minimize2 } from "lucide-react";

// Distinctive palette — cycles if more than 12 players. Avoid red so it
// doesn't compete with the elimination/lose colour used elsewhere.
const PALETTE = [
    "#fde047", "#22d3ee", "#fb923c", "#a78bfa", "#34d399",
    "#f472b6", "#fbbf24", "#60a5fa", "#4ade80", "#e879f9",
    "#f97316", "#06b6d4",
];

const PADDING = { top: 24, right: 160, bottom: 36, left: 44 };

export default function LivingRankChart({ matchSeries, metric = "points", stepPrefix = "M", fallbackValue = null }) {
    const keys = useMemo(() => matchSeries?.keys || [], [matchSeries?.keys]);
    const rows = matchSeries?.rows || [];
    const matches = matchSeries?.matches || [];
    const totalSteps = rows.length;  // baseline + one per match
    const isAvg = metric === "avg"; // iter86.62 — golf LAS: lower is better

    const [mode, setMode] = useState("rank");
    const [step, setStep] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const containerRef = useRef(null);
    const [width, setWidth] = useState(800);

    // iter86.62 — Escape exits fullscreen.
    useEffect(() => {
        if (!fullscreen) return;
        const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [fullscreen]);

    // iter47g — When matchSeries arrives async, snap to the final match.
    useEffect(() => {
        if (totalSteps > 0) setStep(totalSteps - 1);
    }, [totalSteps]);

    // Responsive width — listen to container resize. Re-binds on fullscreen
    // toggle because the portal remounts the DOM node.
    useEffect(() => {
        if (!containerRef.current) return;
        const el = containerRef.current;
        const update = () => setWidth(el.clientWidth || 800);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [fullscreen]);

    // Auto-advance when playing
    useEffect(() => {
        if (!playing) return;
        if (step >= totalSteps - 1) {
            setPlaying(false);
            return;
        }
        const t = setTimeout(() => setStep(s => Math.min(s + 1, totalSteps - 1)), 750);
        return () => clearTimeout(t);
    }, [playing, step, totalSteps]);

    const playerColors = useMemo(() => {
        const out = {};
        keys.forEach((k, i) => { out[k.participant_id] = PALETTE[i % PALETTE.length]; });
        return out;
    }, [keys]);

    // Y-axis scale params
    const playerCount = keys.length;
    const innerH = fullscreen
        ? Math.max(320, (typeof window !== "undefined" ? window.innerHeight : 800) - 260)
        : 320;
    const numericVals = rows.flatMap(r =>
        Object.values(r.points || {}).filter(v => v !== null && v !== undefined));
    const yMin = mode === "rank"
        ? 1
        : isAvg
            ? Math.floor(numericVals.length ? Math.min(...numericVals) : 0)
            : 0;
    const yMax = mode === "rank"
        ? Math.max(2, playerCount)
        : isAvg
            ? Math.ceil(numericVals.length ? Math.max(...numericVals) : 1)
            : Math.max(1, ...rows.map(r => Math.max(0, ...Object.values(r.points || {}).map(v => v ?? 0))));
    const innerW = Math.max(200, width - PADDING.left - PADDING.right);
    const xCount = Math.max(2, totalSteps);
    const xStep = innerW / (xCount - 1);

    const xPos = (i) => PADDING.left + i * xStep;
    const yPos = useCallback((value) => {
        // rank + avg: lower is better → top. points: higher is better → top.
        const lowerIsBetter = mode === "rank" || isAvg;
        const norm = lowerIsBetter
            ? (value - yMin) / Math.max(1, (yMax - yMin))   // 0..1 from top
            : 1 - (value - yMin) / Math.max(1, (yMax - yMin));  // invert for points (high=top)
        return PADDING.top + norm * innerH;
    }, [mode, isAvg, yMin, yMax, innerH]);

    // Build smooth path for each player up to current step
    const buildPath = useCallback((pid) => {
        const points = [];
        for (let i = 0; i <= step; i++) {
            const row = rows[i];
            if (!row) continue;
            let y;
            if (mode === "rank") {
                // iter87.199.89 — Tied-rank jitter. Two crewmates sitting on
                // the exact same rank at a given step used to render both
                // paths on top of each other, leaving the last-drawn colour
                // as the only visible line (leader Fab$ silently overlapped
                // by Saka_P in the M9 recap that surfaced this). Spread
                // tied paths ±2px per position so every line is readable
                // without changing the underlying rank data.
                //
                // iter87.199.92 — Suppress jitter at step 0 (pre-season
                // Start gate) so every crewmate flushes to the same
                // starting rank line. Jittering the Start gate looked
                // like players began the season at different ranks,
                // which is never true — at Start everyone is tied.
                const rank = row.ranks?.[pid] ?? playerCount;
                const jitterAllowed = i > 0;
                const tied = jitterAllowed
                    ? keys.filter(k => (row.ranks?.[k.participant_id] ?? playerCount) === rank)
                    : [];
                const idx = tied.findIndex(k => k.participant_id === pid);
                const jitter = tied.length > 1 ? (idx - (tied.length - 1) / 2) * 4 : 0;
                y = yPos(rank) + jitter;
            } else {
                y = yPos(row.points?.[pid] ?? (isAvg ? (fallbackValue ?? yMax) : 0));
            }
            points.push([xPos(i), y]);
        }
        if (points.length === 0) return "";
        if (points.length === 1) return `M ${points[0][0]} ${points[0][1]}`;
        // Build a smooth Catmull-Rom-ish path with cubic Bezier
        let d = `M ${points[0][0]} ${points[0][1]}`;
        for (let i = 1; i < points.length; i++) {
            const [x0, y0] = points[i - 1];
            const [x1, y1] = points[i];
            const cx = (x0 + x1) / 2;
            d += ` C ${cx} ${y0}, ${cx} ${y1}, ${x1} ${y1}`;
        }
        return d;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, rows, mode, playerCount, xStep, width, yMax, keys]);

    if (!matchSeries || totalSteps < 2) return null;

    const currentRow = rows[step] || {};
    const currentMeta = step > 0 ? matches[step - 1] : null;

    // Right-edge labels — for each player at current step, render the username
    // + rank/points at their current y position. Sort by rank desc so higher
    // ranks paint on top. Then de-collide so tied players don't render on top
    // of each other (iter47g — bumps label y by 20px when within label height).
    const rawLabels = keys.map(k => {
        const v = mode === "rank"
            ? (currentRow.ranks?.[k.participant_id] ?? playerCount)
            : (currentRow.points?.[k.participant_id] ?? (isAvg ? (fallbackValue ?? yMax) : 0));
        return {
            pid: k.participant_id,
            username: k.username,
            value: v,
            y: yPos(v),
            x: xPos(step),
            color: playerColors[k.participant_id],
            rank: currentRow.ranks?.[k.participant_id] ?? playerCount,
            pts: currentRow.points?.[k.participant_id] ?? (isAvg ? null : 0),
        };
    }).sort((a, b) => a.y - b.y);

    // Vertical de-collision pass — every label gets at least LABEL_GAP px of
    // vertical breathing room from the previous one.
    const LABEL_GAP = 20;
    const labelsAtCurrent = rawLabels.map((lbl, i, arr) => {
        if (i === 0) return { ...lbl, labelY: lbl.y };
        const prevLabelY = arr[i - 1].labelY ?? arr[i - 1].y;
        const labelY = Math.max(lbl.y, prevLabelY + LABEL_GAP);
        arr[i].labelY = labelY;  // mutate so next iteration can read
        return { ...lbl, labelY };
    });

    const yTicks = mode === "rank"
        ? Array.from({ length: playerCount }, (_, i) => i + 1)
        : (() => {
            const ticks = 5;
            return Array.from({ length: ticks }, (_, i) =>
                Math.round(yMin + ((yMax - yMin) / (ticks - 1)) * i));
        })();

    const svgHeight = innerH + PADDING.top + PADDING.bottom;
    const svgWidth = innerW + PADDING.left + PADDING.right;

    const chartBody = (
        <div
            className={fullscreen
                ? "fixed inset-0 z-[100] bg-ink p-4 overflow-y-auto w-full"
                : "w-full"}
            data-testid="living-rank-chart"
        >
            {/* Controls */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        data-testid="living-chart-play-btn"
                        onClick={() => {
                            if (step >= totalSteps - 1) setStep(0);
                            setPlaying(p => !p);
                        }}
                        className="brutal-btn-primary text-xs py-1.5 px-2.5"
                        aria-label={playing ? "Pause" : "Play"}
                    >
                        {playing
                            ? <><Pause className="w-3 h-3" /> Pause</>
                            : <><Play className="w-3 h-3" /> Play</>}
                    </button>
                    <button
                        type="button"
                        data-testid="living-chart-restart-btn"
                        onClick={() => { setPlaying(false); setStep(0); }}
                        className="brutal-btn-ghost text-xs py-1.5 px-2.5"
                        aria-label="Restart"
                    >
                        <RotateCcw className="w-3 h-3" />
                    </button>
                </div>
                <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-[400px]">
                    <input
                        type="range"
                        min={0}
                        max={totalSteps - 1}
                        value={step}
                        onChange={(e) => { setPlaying(false); setStep(Number(e.target.value)); }}
                        className="flex-1 accent-cyber"
                        data-testid="living-chart-scrub"
                    />
                    <span className="text-[10px] uppercase font-bold text-zinc-500 tabular-nums whitespace-nowrap">
                        {step === 0 ? "Start" : `${stepPrefix}${step}`} / {stepPrefix}{totalSteps - 1}
                    </span>
                </div>
                <div className="flex gap-1 items-center">
                    {/* iter87.199.92 — Points/Avg toggle deprecated per
                        operator direction. Rank is the only mode players
                        find meaningful; points/avg cluttered mobile and
                        confused the timeline. Mode state stays `rank` and
                        `setMode` is unused now but kept in place to avoid
                        churning the surrounding downstream logic. */}
                    <button
                        type="button"
                        data-testid="living-chart-fullscreen-btn"
                        onClick={() => setFullscreen(f => !f)}
                        className="brutal-btn-ghost text-xs py-1.5 px-2.5"
                        aria-label={fullscreen ? "Exit fullscreen" : "Fullscreen"}
                    >
                        {fullscreen ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
                    </button>
                </div>
            </div>

            {/* Match-meta caption */}
            <AnimatePresence mode="wait">
                <motion.p
                    key={step}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-xs text-zinc-400 mb-2 min-h-[1.25em]"
                    data-testid="living-chart-caption"
                >
                    {currentMeta
                        ? <>
                            <span className="text-cyber font-bold tabular-nums">{stepPrefix}{currentMeta.index}</span>
                            {" · "}
                            <span className="text-white">{currentMeta.label}</span>
                            <span className="font-mono text-zinc-300 ml-1">{currentMeta.score}</span>
                          </>
                        : <span className="italic">Tournament hasn't started — everyone's tied.</span>}
                </motion.p>
            </AnimatePresence>

            {/* The chart */}
            <div ref={containerRef} className="overflow-x-auto">
                <svg
                    width={svgWidth}
                    height={svgHeight}
                    viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                    role="img"
                    aria-label={`${mode === "rank" ? "Rank" : "Points"} over time chart`}
                >
                    {/* iter87.58 — Reusable line glow filter for the
                        bento visual refresh. Soft coloured aura per
                        line replaces the hard "#000" dot stroke that
                        made the chart read as brutalist.

                        iter87.202 — filterUnits="objectBoundingBox" (the
                        default) sizes x/y/width/height as % of the
                        FILTERED PATH's own bounding box. A player who
                        holds the exact same rank for the whole chart
                        (perfectly flat line) has a zero-height bbox, so
                        the filter region collapses to zero area — and
                        per SVG spec an empty filter region renders the
                        element as nothing. That's why an all-time #1
                        (or any dead-flat line) silently disappeared
                        while every line with any rank movement rendered
                        fine. userSpaceOnUse ties the region to the fixed
                        SVG canvas instead of the path's own bbox. */}
                    <defs>
                        <filter id="livingRankGlow" filterUnits="userSpaceOnUse"
                            x={-20} y={-20} width={svgWidth + 40} height={svgHeight + 40}>
                            <feGaussianBlur in="SourceGraphic" stdDeviation="2.4" result="blurred" />
                            <feMerge>
                                <feMergeNode in="blurred" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>
                    {/* Y-axis grid + ticks */}
                    {yTicks.map((t, i) => {
                        const y = yPos(t);
                        return (
                            <g key={`y-${i}`}>
                                <line x1={PADDING.left} x2={PADDING.left + innerW}
                                    y1={y} y2={y}
                                    stroke="rgba(255,255,255,0.06)" strokeDasharray="3 5" />
                                <text x={PADDING.left - 8} y={y + 3}
                                    textAnchor="end" fill="#71717a" fontSize="10"
                                    fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                                    style={{ letterSpacing: "0.06em" }}>
                                    {mode === "rank" ? `#${t}` : t}
                                </text>
                            </g>
                        );
                    })}
                    {/* X-axis ticks every few matches */}
                    {Array.from({ length: totalSteps }, (_, i) => i)
                        .filter(i => totalSteps <= 14 || i === 0 || i === totalSteps - 1 || i % Math.ceil(totalSteps / 8) === 0)
                        .map(i => (
                            <text key={`x-${i}`}
                                x={xPos(i)} y={PADDING.top + innerH + 18}
                                textAnchor="middle" fill="#71717a" fontSize="10"
                                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                                style={{ letterSpacing: "0.08em", textTransform: "uppercase" }}>
                                {i === 0 ? "Start" : `${stepPrefix}${i}`}
                            </text>
                        ))}
                    {/* Vertical "current step" marker */}
                    <line
                        x1={xPos(step)} x2={xPos(step)}
                        y1={PADDING.top - 6} y2={PADDING.top + innerH + 4}
                        stroke="rgba(250,255,0,0.18)" strokeWidth={3}
                        strokeLinecap="round"
                    />
                    {/* Player lines — animated d via framer-motion */}
                    {keys.map((k) => (
                        <motion.path
                            key={k.participant_id}
                            d={buildPath(k.participant_id)}
                            fill="none"
                            stroke={playerColors[k.participant_id]}
                            strokeWidth={2.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            filter="url(#livingRankGlow)"
                            initial={false}
                            animate={{ d: buildPath(k.participant_id) }}
                            transition={{ duration: 0.55, ease: "easeInOut" }}
                        />
                    ))}
                    {/* Dot + label at the current step for each crewmate */}
                    {labelsAtCurrent.map((lbl) => {
                        const leaderNeeded = Math.abs((lbl.labelY ?? lbl.y) - lbl.y) > 2;
                        return (
                            <g key={lbl.pid}>
                                {/* Tiny leader line connecting the dot to its
                                    pushed-down label, only drawn when ties
                                    forced the label off its real y. */}
                                {leaderNeeded && (
                                    <line
                                        x1={lbl.x + 4}
                                        y1={lbl.y}
                                        x2={lbl.x + 10}
                                        y2={lbl.labelY}
                                        stroke={lbl.color}
                                        strokeWidth={1}
                                        strokeDasharray="2 3"
                                        opacity={0.55}
                                    />
                                )}
                                <motion.circle
                                    cx={lbl.x}
                                    cy={lbl.y}
                                    r={5}
                                    fill={lbl.color}
                                    stroke="rgba(9,9,11,0.9)"
                                    strokeWidth={2}
                                    animate={{ cx: lbl.x, cy: lbl.y }}
                                    transition={{ duration: 0.55, ease: "easeInOut" }}
                                />
                                <g transform={`translate(${lbl.x + 10},${lbl.labelY})`}>
                                    {/* iter87.58 — Bento-style glass label pill.
                                        Rounded corners (rx=10), subtle
                                        colour-tinted ring instead of a hard
                                        1px border. Fill is deep translucent
                                        so it reads as a floating chip on the
                                        chart, not a boxed sticker. */}
                                    <rect x={0} y={-11} width={148} height={22}
                                        rx={10}
                                        fill="rgba(9,9,11,0.85)"
                                        stroke={lbl.color}
                                        strokeOpacity={0.55}
                                        strokeWidth={1} />
                                    <text x={8} y={4} fill={lbl.color}
                                        fontSize={11} fontWeight={700}
                                        fontFamily="system-ui">
                                        {String(lbl.username).slice(0, 12)}
                                    </text>
                                    <text x={140} y={4} fill="#f4f4f5" fontSize={10}
                                        textAnchor="end"
                                        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                                        fontWeight={700}>
                                        {isAvg
                                            ? (mode === "rank"
                                                ? `#${lbl.rank} · ${lbl.pts ?? "—"}`
                                                : `${lbl.pts ?? "—"} · #${lbl.rank}`)
                                            : (mode === "rank"
                                                ? `#${lbl.rank} · ${lbl.pts}p`
                                                : `${lbl.pts}p · #${lbl.rank}`)}
                                    </text>
                                </g>
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>
    );

    // iter86.62 — Portal fullscreen to <body>: ancestor cards use transforms
    // which would otherwise trap position:fixed inside their stacking context.
    return fullscreen ? createPortal(chartBody, document.body) : chartBody;
}
